import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { usePermissions } from "../../../../Context/PermissionContext";
import DeviceIDLink from "../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";

const normalizeColumnsConfig = (columnsConfig) =>
  Object.fromEntries(
    Object.entries(columnsConfig).map(([k, cfg]) => [
      k,
      { ...cfg, dataIndex: cfg.dataIndex || cfg.key || k },
    ])
  );

export const useAccessGrantedEntitiesColumns = (tableConfig, onBusNumberClick) => {
  const { hasPermission, isRbacV2Enabled } = usePermissions();

  const onBusNumberClickRef = useRef(onBusNumberClick);
  onBusNumberClickRef.current = onBusNumberClick;

  const [allColumns, setAllColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);

  const shouldShowColumn = useCallback(
    (config) => {
      const codes = config?.controlledByFeatureCodes;
      if (!codes?.length) return true;
      return !isRbacV2Enabled() || codes.every((code) => hasPermission(code, "view"));
    },
    [hasPermission, isRbacV2Enabled]
  );

  // device-id-link needs an onTextClick handler not available in the default renderer
  const customRenderers = useMemo(
    () => ({
      "device-id-link": (record, config) => (
        <DeviceIDLink
          record={record}
          config={{ ...config, disableLink: false }}
          onTextClick={() => onBusNumberClickRef.current?.(record)}
        />
      ),
    }),
    [] // stable — uses ref internally
  );

  useEffect(() => {
    const { columnOrdering = [], columnsConfig = {} } = tableConfig || {};
    const nextColumns = getColumnsConfig({
      tableColumns: normalizeColumnsConfig(columnsConfig),
      tableColumnOrder: columnOrdering,
      shouldShowColumn,
      customRenderers,
    });
    setAllColumns(nextColumns);
    setVisibleColumns(nextColumns);
  }, [tableConfig, shouldShowColumn, customRenderers]);

  const applySelectedColumns = (newSelectedKeys) => {
    const filtered = allColumns.filter((col) => newSelectedKeys.includes(col.dataIndex));
    setVisibleColumns(filtered);
  };

  return {
    allColumns,
    visibleColumns,
    setVisibleColumns,
    selectedColumnKeys: visibleColumns.map((col) => col.dataIndex),
    defaultColumnKeys: allColumns.map((col) => col.dataIndex),
    applySelectedColumns,
  };
};
